function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext } from 'react';
import cn from 'clsx';
import { arrayOf, bool, string, shape } from 'prop-types';
import List from '../List/index.jsx';
import MenuLink from '../../atoms/MenuLink/index.jsx';
import withAccessibleMenu from '../../hoc/withAccessibleMenu/index.js';
import PopupContext from '../../context/PopupContext.js';
import Icon from '../../atoms/Icon/index.jsx';
import { useCurrentUrl } from '../../hooks/index.js';

// Function to ensure that the currentUrl ends with a slash
const ensureUrlEndsWithSlash = url => {
  if (!(url === null || url === void 0 ? void 0 : url.endsWith('/'))) {
    return `${url}/`;
  }
  return url;
};

/**
 * VerticalNav component.
 */
const VerticalNav = withAccessibleMenu(({
  accessibleMenuItems,
  ariaLabel,
  className,
  domId,
  showActiveClass,
  showMenuTopBorder,
  showSubmenu
}) => {
  const {
    setActivePopup
  } = useContext(PopupContext);
  const currentUrl = useCurrentUrl();
  const normalisedUrl = ensureUrlEndsWithSlash(currentUrl);
  return /*#__PURE__*/React.createElement("nav", {
    className: cn('vertical-nav', className),
    id: domId,
    "aria-label": ariaLabel
  }, /*#__PURE__*/React.createElement(List, {
    className: 'vertical-nav__list' + (!showMenuTopBorder ? " vertical-nav__list--border-top-none" : ""),
    role: accessibleMenuItems.length ? 'menubar' : null
  }, accessibleMenuItems.map(tierOneItem => {
    var _tierOneItem$children;
    const hasActiveChild = normalisedUrl && (tierOneItem === null || tierOneItem === void 0 ? void 0 : (_tierOneItem$children = tierOneItem.children) === null || _tierOneItem$children === void 0 ? void 0 : _tierOneItem$children.some(child => {
      const {
        absoluteUrl,
        relativeUrl
      } = child.data;
      if (absoluteUrl) {
        return ensureUrlEndsWithSlash(absoluteUrl) === normalisedUrl;
      }
      return normalisedUrl.endsWith(ensureUrlEndsWithSlash(relativeUrl));
    }));
    const activeClass = showActiveClass && !hasActiveChild ? 'vertical-nav__nav-link--active' : null;
    const activeParentClass = showActiveClass && hasActiveChild ? 'vertical-nav__nav-link--active-parent' : null;
    return /*#__PURE__*/React.createElement(List.Item, {
      className: 'vertical-nav__nav-item',
      key: tierOneItem.key,
      role: "presentation"
    }, /*#__PURE__*/React.createElement(MenuLink, _extends({
      className: 'vertical-nav__nav-link pt-xs pb-xs pl-sm pr-sm body-copy-small',
      activeClass: activeClass,
      activeParentClass: activeParentClass,
      role: "menuitem"
    }, tierOneItem.linkProps, {
      onClick: evt => {
        tierOneItem.linkProps.onClick(evt);
        setActivePopup(null);
      },
      onKeyDown: evt => {
        const {
          keyCode
        } = evt;
        tierOneItem.linkProps.onKeyDown(evt);

        // Close the popup if enter or escape pressed
        if (keyCode === 13 || keyCode === 27) {
          setActivePopup(null);
        }
      }
    }), tierOneItem.data.icon && /*#__PURE__*/React.createElement(Icon, {
      name: tierOneItem.data.icon,
      color: "inherit",
      className: "mr-xs vertical-nav__nav-icon"
    }), tierOneItem.data.label), tierOneItem.hasChildren && showSubmenu && /*#__PURE__*/React.createElement(List, {
      className: "vertical-nav__submenu",
      role: tierOneItem.children.length ? 'menu' : null
    }, tierOneItem.children.map(tierTwoItem => /*#__PURE__*/React.createElement(List.Item, {
      className: 'vertical-nav__nav-item',
      role: "presentation",
      key: tierTwoItem.key
    }, /*#__PURE__*/React.createElement(MenuLink, _extends({
      className: "vertical-nav__nav-link pl-lg pr-lg pt-xs pb-xs body-copy-small",
      role: "menuitem"
    }, tierTwoItem.linkProps, {
      activeClass: showActiveClass ? 'vertical-nav__nav-link--tier-two--active' : null,
      onClick: evt => {
        tierTwoItem.linkProps.onClick(evt);
        setActivePopup(null);
      },
      onKeyDown: evt => {
        const {
          keyCode
        } = evt;
        tierTwoItem.linkProps.onKeyDown(evt);

        // Close the popup if enter or escape pressed
        if (keyCode === 13 || keyCode === 27) {
          setActivePopup(null);
        }
      }
    }), tierTwoItem.data.icon && /*#__PURE__*/React.createElement(Icon, {
      name: tierTwoItem.data.icon,
      color: "inherit",
      className: "mr-xs",
      svgClassName: "vertical-nav__nav-icon"
    }), tierTwoItem.data.label)))));
  })));
});
VerticalNav.displayName = 'VerticalNav';
VerticalNav.defaultProps = {
  ariaLabel: 'menu',
  className: null,
  domId: '',
  menuItems: [],
  showActiveClass: false,
  showSubmenu: false,
  showMenuTopBorder: false
};
VerticalNav.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Aria label for the menu. */
  ariaLabel: string,
  /** Additional class names to add to the component root. */
  className: string,
  /** ID to identify the DOM element */
  domId: string,
  /** Array of menu items */
  menuItems: arrayOf(shape(MenuLink.propTypes)),
  /** Whether the active class is shown */
  showActiveClass: bool,
  /** Whether the child items are rendered */
  showSubmenu: bool,
  /** Whether to show a top border on the menu */
  showMenuTopBorder: bool
} : {};
export default VerticalNav;